import LText from "@components/LText";
import BrowseItem from "@components/Search/BrowseItem";
import BottomBarPadding from "@components/BottomBarPadding";
import { useGenres } from "@hooks/trpc/useGenres";
import LBox from "@components/LBox";
import { theme } from "@theme";
import { RouterOutputs } from "@utils/trpc";
import React, { useEffect, useMemo, useRef } from "react";
import { ActivityIndicator, Pressable } from "react-native";
import { captureException } from "@utils/captureException";
import { addBreadcrumb } from "@utils/sentry";
import { DJ_SETS_CATEGORY } from "./djSetsCategory";
const DEFAULT_GENRE_LIMIT = 10;

type BrowseGenresProps = {
  renderListHeader?: () => React.ReactNode;
};

const BrowseGenres = ({ renderListHeader }: BrowseGenresProps) => {
  const {
    query: {
      data: paginatedGenres,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      isLoading: areGenresLoading,
      isError: didGenresError,
      refetch: refetchGenres,
    },
  } = useGenres(DEFAULT_GENRE_LIMIT);

  // Note: onEndReached can be triggered multiple times in very quick succession during
  // a list scroll. However, we don't want to repeat the same fetch queries for the
  // same cursor, so we use a ref to flag if a fetch has previously been triggered.
  const endReachedRef = useRef(false);
  useEffect(() => {
    endReachedRef.current = isFetchingNextPage;
  }, [isFetchingNextPage]);

  const genres = useMemo(() => {
    const loaded = paginatedGenres?.pages.flatMap((page) => page.data) ?? [];
    // Pin the DJ Sets category tile at index 0 once genres are present —
    // injected only alongside real data so the loading/empty/error states
    // (and their Sentry breadcrumbs below) stay driven by the genres query.
    return loaded.length > 0 ? [DJ_SETS_CATEGORY, ...loaded] : loaded;
  }, [paginatedGenres?.pages]);
  const genreColumns = useMemo(() => {
    return genres.reduce<[typeof genres, typeof genres]>(
      (columns, genre, index) => {
        columns[index % 2].push(genre);
        return columns;
      },
      [[], []],
    );
  }, [genres]);

  // Instrument the genres section's render state so a recurrence of the
  // "blank genres" bug is visible in Sentry session replay / breadcrumbs
  // (the signal we lacked when first diagnosing it). Emits only on change.
  // See the LIS-102 PR report for the original debugging context.
  const genresState =
    didGenresError && genres.length === 0
      ? "error"
      : genres.length > 0
        ? "ok"
        : areGenresLoading
          ? "loading"
          : "empty";
  const lastLoggedStateRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastLoggedStateRef.current === genresState) return;
    lastLoggedStateRef.current = genresState;
    addBreadcrumb({
      category: "genres",
      message: `BrowseGenres render: ${genresState}`,
      level:
        genresState === "error" || genresState === "empty" ? "warning" : "info",
      data: { state: genresState, count: genres.length },
    });
  }, [genresState, genres.length]);

  useEffect(() => {
    if (!hasNextPage || endReachedRef.current) {
      return;
    }

    endReachedRef.current = true;
    fetchNextPage()
      .catch((error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "pagination",
          operation: "fetch-next-search-browse",
        });
      })
      .finally(() => {
        endReachedRef.current = false;
      });
  }, [fetchNextPage, hasNextPage]);

  const renderBrowseGenreHeader = () => {
    return (
      <>
        {renderListHeader ? renderListHeader() : null}
        <LText marginVertical="m1" fontWeight="bold" variant="bodyL">
          Browse genres
        </LText>
      </>
    );
  };

  // Render whatever genres we already have (incl. durable-cache initialData)
  // even while a background fetch is in flight — only show the full-section
  // spinner when we have nothing yet. Gating on data presence rather than
  // `isLoading` is what prevents the "blank genres section" bug from a
  // stuck/empty loading state. See the LIS-102 PR report for context.
  if (genres.length === 0) {
    if (didGenresError) {
      return (
        <Pressable
          onPress={() => {
            refetchGenres().catch((error) => {
              captureException(error, {
                channel: "breadcrumb",
                area: "query",
                operation: "retry-browse-genres",
              });
            });
          }}
          accessibilityRole="button"
        >
          <LText marginVertical="m1" variant="bodyL">
            Couldn&apos;t load genres. Tap to retry.
          </LText>
        </Pressable>
      );
    }
    if (areGenresLoading) {
      return <ActivityIndicator size="large" />;
    }
  }

  return (
    <>
      {renderBrowseGenreHeader()}
      <LBox flexDirection="row" alignItems="flex-start">
        {genreColumns.map((column, columnIndex) => (
          <LBox key={`genre-column-${columnIndex}`} flex={1}>
            {column.map((genre, columnItemIndex) => {
              const index = columnItemIndex * 2 + columnIndex;
              return (
                <BrowseItem
                  genre={
                    genre as RouterOutputs["catalog"]["genres"]["listPaginated"]["data"][number]
                  }
                  index={index}
                  key={genre.id}
                />
              );
            })}
          </LBox>
        ))}
      </LBox>
      {isFetchingNextPage ? (
        <ActivityIndicator
          size="large"
          style={{ paddingBottom: theme.spacing.s4 }}
        />
      ) : null}
      <BottomBarPadding />
    </>
  );
};

export default BrowseGenres;
